/**
 * "Mémoire des lieux" landing page — Histoire du Prieuré saint Léonard.
 *
 * Réutilise le contenu déjà rédigé sur `Home.jsx` (section #histoire) mais
 * dans une page autonome pour le partage direct et le SEO, et pour rassembler
 * sous "Mémoire des lieux" les récits fondateurs du site.
 */
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";

const HISTORY_IMG = "/photos/prieure-cross.jpg";

export default function Histoire() {
  const { locale, t } = useI18n();
  const isFr = locale === "fr";
  return (
    <>
      <SeoHead
        titles={{
          fr: "Histoire du Prieuré saint Léonard — Mémoire des lieux",
          en: "History of the Priory of Saint Léonard — Memory of the place",
        }}
        descriptions={{
          fr: "Mille ans d'histoire en baie du Mont-Saint-Michel : fondé en 1027, au siècle de Guillaume le Conquérant et de Mathilde de Flandre, le Prieuré saint Léonard est une étape de la route historique des Abbayes de Normandie, de Caen au Mont-Saint-Michel.",
          en: "A thousand years of history on the Bay of Mont-Saint-Michel: founded in 1027, in the century of William the Conqueror and Matilda of Flanders, the Priory of Saint Léonard is a stage on the historic route of the Abbeys of Normandy, from Caen to Mont-Saint-Michel.",
        }}
        keywords="histoire Prieuré saint Léonard, monument historique, 1027, Guillaume le Conquérant, Mathilde de Flandre, Caen, abbayes de Normandie route historique, chapelle romane, patrimoine normand, millénaire, Mont-Saint-Michel, Avranches, Normandie médiévale"
      />
      <div style={{ background: "var(--parchment)" }}>
        {/* Small hero */}
        <section className="py-16 sm:py-20 px-6 text-center border-b" style={{ borderColor: "var(--gold)" }}>
          <Ornament />
          <p className="font-caps text-[0.72rem] mt-6" style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.2em" }}>
            {isFr ? "MÉMOIRE DES LIEUX" : "MEMORY OF THE PLACE"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">
            {t.home.historyTitle}
          </h1>
        </section>

        {/* Full history content (mirrors Home.jsx #histoire) */}
        <section className="py-20 sm:py-24" style={{ background: "var(--parchment-2)" }}>
          <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-24">
              <img src={HISTORY_IMG} alt="" className="w-full object-cover" style={{ aspectRatio: "4/5" }} />
              <p className="font-body italic text-sm mt-3" style={{ color: "var(--muted)" }}>
                {isFr ? "Arcades de pierre — architecture monastique normande" : "Stone arcades — Norman monastic architecture"}
              </p>
            </div>
            <div className="md:col-span-7 space-y-12">
              {t.home.historyChapters.map((c, i) => (
                <article
                  key={i}
                  data-testid={`histoire-chapter-${i}`}
                  className="border-l-2 pl-6"
                  style={{ borderColor: i === 0 ? "var(--cinnabar)" : "var(--gold)" }}
                >
                  <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--ink-2)" }}>
                    {`I${"I".repeat(i)}`.slice(0, 5)}
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl mb-3">{c.title}</h3>
                  <p className="font-body text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
                    {c.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
